import {
  GenericEntityLockButton,
  GenericEntityLockButtonProps,
} from "../../../Entities/GenericComponents/common/GenericEntityLockButton";
import {
  GenericEntitySignatureInformationHistoryButton,
  GenericEntitySignatureInformationHistoryButtonProps,
} from "../../../Entities/GenericComponents/common/GenericEntitySignatureInformationHistoryButton";
import {
  GenericEntitySignButton,
  GenericEntitySignButtonProps,
} from "../../../Entities/GenericComponents/common/GenericEntitySignButton";
import {
  GenericEntityUnlockButton,
  GenericEntityUnlockButtonProps,
} from "../../../Entities/GenericComponents/common/GenericEntityUnlockButton";
import { GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { GenericEntity, GenericEntityComponentProps } from "../../../Entities/GenericComponents/GenericComponents";
import { ILockableEntity, ISignableEntity, ISoftDeletable } from "../../../api/GenericTypes";
import { NestedDropdown } from "../../buttons/NestedDropdown/NestedDropdown";

interface EntityComplianceDropdownProps<
  Entity extends GenericEntity & Partial<ILockableEntity> & Partial<ISignableEntity> & Partial<ISoftDeletable>
> {
  entityTypeId: GenericEntityConstantsEntities;
  entity: Entity;
  showSignButton?: boolean; // Optional prop to control the visibility of the sign button
  propOverrides?: {
    GenericEntityLockButtonProps?: Omit<
      GenericEntityComponentProps<Entity, GenericEntityLockButtonProps<Entity>>,
      "entityTypeId" | "entity"
    >;
    GenericEntityUnlockButtonProps?: Omit<
      GenericEntityComponentProps<Entity, GenericEntityUnlockButtonProps<Entity>>,
      "entityTypeId" | "entity"
    >;
    GenericEntitySignButtonProps?: Omit<
      GenericEntityComponentProps<Entity, GenericEntitySignButtonProps<Entity>>,
      "entityTypeId" | "entity"
    >;
    GenericEntitySignatureInformationHistoryButtonProps?: Omit<
      GenericEntityComponentProps<Entity, GenericEntitySignatureInformationHistoryButtonProps>,
      "entityTypeId" | "entity"
    >;
  };
}

/**
 * Renders a dropdown menu for compliance actions (locking, unlocking, signing, and signature history)
 * for a given entity. The dropdown adapts its content based on the `showSignButton` prop.
 *
 * - If the entity is marked as deleted (`isDeleted`), the dropdown is not rendered.
 * - If `showSignButton` is `false`, only lock and unlock buttons are shown as list items.
 * - If `showSignButton` is `true`, a submenu is rendered with lock, sign, unlock, and signature history buttons.
 *   The submenu can be toggled open/closed, and closes automatically when any action is performed.
 *
 * @typeParam Entity - The entity type, extending `GenericEntity` and optionally supporting lockable, signable, and soft-deletable interfaces.
 * @param props.entityTypeId - The type identifier for the entity.
 * @param props.entity - The entity instance to operate on.
 * @param [props.showSignButton=false] - Whether to show the signing-related actions in the dropdown.
 * @param [props.propOverrides] - Optional overrides for the props of the individual action buttons.
 * @returns A React element representing the compliance dropdown menu, or an empty fragment if the entity is deleted.
 */
export const EntityComplianceDropdown = <
  Entity extends GenericEntity & Partial<ILockableEntity> & Partial<ISignableEntity> & Partial<ISoftDeletable>
>({
  entityTypeId,
  entity,
  showSignButton = false, // Default to false if not provided
  propOverrides,
}: EntityComplianceDropdownProps<Entity>) => {
  if (Object.hasOwn(entity, "isDeleted") && entity.isDeleted) return <></>; // Do not show the dropdown if the entity is deleted

  if (!showSignButton)
    return (
      <>
        <li>
          <GenericEntityLockButton entityTypeId={entityTypeId} entity={entity} />
        </li>
        <li>
          <GenericEntityUnlockButton entityTypeId={entityTypeId} entity={entity} />
        </li>
      </>
    );

  return (
    <NestedDropdown label={showSignButton ? "Locking / Signing" : "Locking / Unlocking"} icon={"clipboard-check"}>
      <li>
        <GenericEntityLockButton
          {...propOverrides?.GenericEntityLockButtonProps}
          entityTypeId={entityTypeId}
          entity={entity}
          componentProps={{
            ...propOverrides?.GenericEntityLockButtonProps?.componentProps,
          }}
        />
      </li>
      <li>
        <GenericEntitySignButton
          {...propOverrides?.GenericEntitySignButtonProps}
          entityTypeId={entityTypeId}
          entity={entity}
          componentProps={{
            ...propOverrides?.GenericEntitySignButtonProps?.componentProps,
          }}
        />
      </li>
      <li>
        <GenericEntityUnlockButton
          {...propOverrides?.GenericEntityUnlockButtonProps}
          entityTypeId={entityTypeId}
          entity={entity}
          componentProps={{
            ...propOverrides?.GenericEntityUnlockButtonProps?.componentProps,
          }}
        />
      </li>
      <li>
        <GenericEntitySignatureInformationHistoryButton
          {...propOverrides?.GenericEntitySignatureInformationHistoryButtonProps}
          entity={entity}
          entityTypeId={entityTypeId}
          componentProps={{
            ...propOverrides?.GenericEntitySignatureInformationHistoryButtonProps?.componentProps,
          }}
        />
      </li>
    </NestedDropdown>
  );
};
